import { useCanSeeAspenWeb, useImpersonator } from '@solinkcorp/authentication';
import { Grid, useWindowSize } from '@solinkcorp/solink-components';
import ThemeVariables from 'env/themeVariables';
import React, { useContext, useLayoutEffect } from 'react';
import { setColorThemeAspenShell } from 'src/hooks/useColorTheme';
import useFluxStore from 'src/hooks/useFluxStore';
import { LocationStore } from 'stores/LocationStore';
import FeaturesUtil from 'utils/FeaturesUtil';
import isShallowEqual from 'utils/isShallowEqual';
import AppRoutes from '../AppRoutes';
import { CommonContext, VideoContext } from '../Contexts';
import DesktopHeader from './DesktopHeader/DesktopHeader';
import DesktopNav from './DesktopNav/DesktopNav';
import { type NavTabWithComponent } from './menu';
import SidebarNavigationFrame from './navigation/SidebarNavigationFrame';

interface NavigationFrameProps {
  updateLiveToggle: (toggle: boolean) => void;
  onShowAllNotifications: () => void;
  inboxCount: number;
  navigation: NavTabWithComponent[];
  defaultPath: string;
}

const NavigationFrame: React.FC<NavigationFrameProps> = (props) => {
  const useSidebarNavigation = useCanSeeAspenWeb();

  // Temporary: the theme toggle is only on the Aspen rail. Keep the stored
  // preference, but apply `.dark` only while this shell is showing so a user
  // who loses aspenWeb is not stuck dark with no control. Remove this effect
  // (and setColorThemeAspenShell) once everyone is on Aspen.
  //
  // The cleanup strips `.dark` when this frame unmounts. Full-screen siblings
  // rendered outside NavigationFrame (video wall, SDM, public share/alarm) have
  // no rail toggle, so leaving the shell for one of them must clear the class —
  // otherwise those surfaces keep dark tokens with no way to switch back.
  useLayoutEffect(() => {
    setColorThemeAspenShell(useSidebarNavigation);
    return () => {
      setColorThemeAspenShell(false);
    };
  }, [useSidebarNavigation]);

  return useSidebarNavigation ? (
    <SidebarNavigationFrame {...props} />
  ) : (
    <LegacyNavigationFrame {...props} />
  );
};

/** The 80px icon rail and full-width header, in use until the redesign ships. */
const LegacyNavigationFrame: React.FC<NavigationFrameProps> = ({
  updateLiveToggle,
  onShowAllNotifications,
  inboxCount,
  navigation,
  defaultPath,
}) => {
  const isImpersonating = !!useImpersonator();

  const windowSize = useWindowSize();

  const { isLiveToggleOn } = useContext(VideoContext);
  const { appState } = useContext(CommonContext);

  const { locations, locationsById } = useFluxStore(
    LocationStore,
    (store) => ({
      locations: store.getLocations(),
      locationsById: store.getLocationsById(),
    }),
    isShallowEqual,
  );

  const { desktopNavWidth, desktopTopNavHeight } = ThemeVariables;

  const canAccessWeb = FeaturesUtil.canAccessWeb();

  return (
    <Grid
      height="100vh"
      width="100vw"
      gridTemplateColumns={`${desktopNavWidth}px ${windowSize.width - desktopNavWidth}px`}
      gridTemplateRows={`${desktopTopNavHeight}px ${
        windowSize.height - desktopTopNavHeight - appState.bannerHeightPx
      }px`}
      className="main"
    >
      {canAccessWeb && (
        <React.Fragment>
          <Grid.Item
            width="100%"
            gridRow={`1 / 2`}
            gridColumn={`2 / 3`}
            style={{ background: '#3F3F3F', paddingRight: '20px' }}
            zIndex={100}
          >
            <DesktopHeader
              handleLiveToggle={updateLiveToggle}
              isImpersonating={isImpersonating}
              isLiveToggleOn={isLiveToggleOn}
              notificationCount={inboxCount}
              onShowAllNotifications={onShowAllNotifications}
              windowWidth={windowSize.width}
              locationsById={locationsById}
              locations={locations}
            />
          </Grid.Item>
          <Grid.Item width="100%" height="100%" gridRow={`1 / 3`} gridColumn={`1 / 2`} zIndex={999}>
            <DesktopNav tabs={navigation} />
          </Grid.Item>
        </React.Fragment>
      )}
      <Grid.Item width="100%" height="100%" gridRow={'2 / 3'} gridColumn={'2 / 3'} zIndex={11}>
        <div className="logged-in" id="vd-app">
          <AppRoutes userPermittedRoutes={navigation} defaultPath={defaultPath} />
        </div>
      </Grid.Item>
    </Grid>
  );
};

export default React.memo(NavigationFrame);
